package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.domain.util.WorkplaceAccessRevokedException
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.roster.ShiftTimeDto
import com.evoapps.evoshift.domain.model.ClaimRefusedException
import com.evoapps.evoshift.domain.model.ClaimRefusal
import com.evoapps.evoshift.domain.model.ClaimOutcome
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.ClockOutcome
import com.evoapps.evoshift.domain.model.LocationCheck
import com.evoapps.evoshift.domain.model.ClockReviewAction
import com.evoapps.evoshift.domain.model.ClockReviewException
import com.evoapps.evoshift.domain.model.UnplannedClockIn
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.model.LocationReading
import com.evoapps.evoshift.domain.model.LocationUnavailable
import com.evoapps.evoshift.domain.usecase.shift.ClockException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.combine
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import com.evoapps.evoshift.domain.model.ShiftOverlap
import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.TimeRange
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import com.evoapps.evoshift.data.dto.roster.ShiftDto
import com.evoapps.evoshift.data.dto.roster.ShiftManagerNotesDto
import com.evoapps.evoshift.domain.model.ShiftManagerNotes
import com.evoapps.evoshift.data.mapper.ConflictMetadataMapper
import com.evoapps.evoshift.data.mapper.MetadataMapper
import com.evoapps.evoshift.data.mapper.ShiftMapper
import com.evoapps.evoshift.data.mapper.SwapRequestMapper
import com.evoapps.evoshift.domain.model.ConflictMetadata
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.RemovedShift
import com.evoapps.evoshift.domain.model.RosterPublishing
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import com.evoapps.evoshift.domain.model.ownSchedule
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map

class ShiftRepositoryImpl(
    private val firestore: AppFirestore,
    private val shiftMapper: ShiftMapper,
    private val swapRequestMapper: SwapRequestMapper,
    private val conflictMetadataMapper: ConflictMetadataMapper,
    private val metadataMapper: MetadataMapper,
    private val functions: CloudFunctionsClient,
    /** SH-263: who deletes (the removal records' `removedBy`). */
    private val currentUserId: suspend () -> String? = { null }
) : ShiftRepository {

    // Resolved per use, never kept: a reference holds the SDK instance it was made from, and a
    // sign-out terminates that instance (SH-177, AppFirestore).
    private val collection get() = firestore.collection("shifts")

    // --- SH-259: the managers' notes, out of the shift document -----------------------------------

    private fun notesRef(shiftId: String) = firestore.collection(MANAGER_NOTES).document(shiftId)

    override fun observeManagerNotes(rosterId: String): Flow<Map<String, ShiftManagerNotes>> =
        firestore.collection(MANAGER_NOTES).where { "rosterId" equalTo rosterId }.safeSnapshots
            .map { snapshot -> snapshot.documents.map { it.data<ShiftManagerNotesDto>().toDomain() }.associateBy { it.shiftId } }
            // Managers and owners only (firestore.rules): anyone else has none to see.
            .catch { e -> if (e.isPermissionDenied()) emit(emptyMap()) else throw e }
            .distinctUntilChanged()

    override suspend fun getManagerNotes(rosterId: String): Result<Map<String, ShiftManagerNotes>> = runCatching {
        firestore.collection(MANAGER_NOTES).where { "rosterId" equalTo rosterId }.get().documents
            .map { it.data<ShiftManagerNotesDto>().toDomain() }.associateBy { it.shiftId }
    }

    override suspend fun getManagerNote(shiftId: String): Result<ShiftManagerNotes?> = runCatching {
        notesRef(shiftId).get().takeIf { it.exists }?.data<ShiftManagerNotesDto>()?.toDomain()
    }

    /** [notes] as written now by the signed-in manager. */
    private suspend fun notesDto(notes: ShiftManagerNotes): ShiftManagerNotesDto =
        ShiftManagerNotesDto.of(notes, AppClock.now().toString(), runCatching { currentUserId() }.getOrNull().orEmpty())

    /**
     * The notes the screens put on [shifts] (a no-show's reason, a split's note), kept in their own
     * documents after a whole-shift write: the shift document no longer carries them (ShiftMapper),
     * so a note read from what an older app left there is not lost. Only managers read notes, so
     * only their shifts carry any; a refusal leaves the shifts as written.
     */
    private suspend fun keepNotesOf(shifts: List<Shift>) {
        val notes = shifts.mapNotNull(ShiftManagerNotes::of)
        if (notes.isEmpty()) return
        runCatching {
            notes.chunked(ShiftBatches.MAX_WRITES).forEach { chunk ->
                val batch = firestore.batch()
                chunk.forEach { batch.set(notesRef(it.shiftId), notesDto(it)) }
                batch.commit()
            }
        }
    }

    override fun observeShiftsForRoster(rosterId: String): Flow<List<Shift>> {
        return firestore.collection("shifts")
            .where { "rosterId" equalTo rosterId }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map {
                    shiftMapper.toDomain(it.data<ShiftDto>())
                }.sortedBy { it.plannedTime.start } // Sort by start time for better UI
            }
            // SH-188: refused under the rules (the engagement ended, a role changed): a typed error
            // the screen words as "You no longer have access", not an empty month.
            .catch { e -> if (e.isPermissionDenied()) throw WorkplaceAccessRevokedException(e) else throw e }
            // Snapshots fire from the cache and again from the server with the same data.
            .distinctUntilChanged()
    }

    override suspend fun getShiftsForRoster(rosterId: String): Result<List<Shift>> {
        return try {
            val snapshot = collection.where { "rosterId" equalTo rosterId }
                .get()

            val shifts = snapshot.documents.map { doc ->
                shiftMapper.toDomain(doc.data<ShiftDto>())
            }.sortedBy { it.plannedTime.start }

            Result.success(shifts)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** SH-255: the teamRoster function's month (colleagues' copies, functions/rosterPrivacy.js). */
    override suspend fun getTeamRoster(rosterId: String): Result<List<Shift>> = runCatching {
        teamRoster(buildJsonObject { put("rosterId", rosterId) })
    }

    override suspend fun getTeamShift(shiftId: String): Result<Shift> = runCatching {
        teamRoster(buildJsonObject { put("shiftId", shiftId) }).firstOrNull() ?: throw Exception("Shift not found")
    }

    private suspend fun teamRoster(data: JsonObject): List<Shift> {
        val result = functions.call("teamRoster", data)
        return overlapsJson.decodeFromJsonElement(TeamRosterResult.serializer(), result).shifts
            .map { shiftMapper.toDomain(it) }
            .sortedBy { it.plannedTime.start }
    }

    /**
     * SH-255: the person's shifts of one month, by `userId` and by `actualUserId` (both allowed by
     * the rules whatever the workplace shows of colleagues' shifts).
     */
    override suspend fun getOwnShiftsInRoster(rosterId: String, userId: String): Result<List<Shift>> = runCatching {
        listOf("userId", "actualUserId").flatMap { field ->
            collection.where { "rosterId" equalTo rosterId }.where { field equalTo userId }.get()
                .documents.map { shiftMapper.toDomain(it.data<ShiftDto>()) }
        }.distinctBy { it.id }.sortedBy { it.plannedTime.start }
    }

    override fun observeOwnShiftsInRoster(rosterId: String, userId: String): Flow<List<Shift>> =
        combine(ownInRoster(rosterId, "userId", userId), ownInRoster(rosterId, "actualUserId", userId)) { assigned, worked ->
            (assigned + worked).distinctBy { it.id }.sortedBy { it.plannedTime.start }
        }.distinctUntilChanged()

    private fun ownInRoster(rosterId: String, field: String, userId: String): Flow<List<Shift>> =
        firestore.collection("shifts")
            .where { "rosterId" equalTo rosterId }
            .where { field equalTo userId }
            .safeSnapshots
            .map { snapshot -> snapshot.documents.map { shiftMapper.toDomain(it.data<ShiftDto>()) } }
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }

    override fun observeShiftsForUser(userId: String, fromDate: String): Flow<List<Shift>> =
        // One field per query (no composite index); the date is applied here. SH-136: the shifts
        // the person was recorded on (`actualUserId`) are theirs too (firestore.rules let them read
        // those), and a shift a colleague was recorded on is no longer in the assignee's schedule.
        combine(userShifts("userId", userId), userShifts("actualUserId", userId)) { assigned, worked ->
            ownSchedule(assigned, worked, userId).filter { it.date >= fromDate }.sortedBy { it.date }
        }
            // Snapshots fire from the cache and again from the server with the same data.
            .distinctUntilChanged()

    /** Live shifts whose [field] is [userId]; empty when the rules deny the read. */
    private fun userShifts(field: String, userId: String): Flow<List<Shift>> =
        firestore.collection("shifts")
            .where { field equalTo userId }
            .safeSnapshots
            .map { snapshot -> snapshot.documents.map { shiftMapper.toDomain(it.data<ShiftDto>()) } }
            .catch { e ->
                if (e.isPermissionDenied()) {
                    emit(emptyList())
                } else {
                    throw e
                }
            }

    override suspend fun getShift(shiftId: String): Result<Shift> {
        return try {
            val snapshot = firestore.collection("shifts").document(shiftId).get()
            if (snapshot.exists) {
                val dto = snapshot.data<ShiftDto>()
                Result.success(shiftMapper.toDomain(dto))
            } else {
                Result.failure(Exception("Shift not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createShift(shift: Shift): Result<String> {
        return try {
            val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)

            val docRef = firestore.collection("shifts").document
            val newShiftId = docRef.id

            val shiftWithId = shift.copy(id = newShiftId, dailyRecordId = dailyRecordId)
            val dto = shiftMapper.toDto(shiftWithId)
            docRef.set(dto)

            Result.success(newShiftId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createShifts(shifts: List<Shift>): Result<Unit> = createShiftsWithIds(shifts).map { }

    /**
     * Writes [shifts] as new documents; returns their ids in order. SH-197: in batches the rules can
     * check ([ShiftBatches]: at most 10 different people each). All or nothing: when a later batch
     * fails, the batches already written are deleted again before the failure is returned.
     */
    override suspend fun createShiftsWithIds(shifts: List<Shift>): Result<List<String>> {
        val ids = mutableListOf<String>()
        val written = mutableListOf<String>()
        return try {
            ShiftBatches.split(shifts).forEach { chunk ->
                val batch = firestore.batch()
                val chunkIds = mutableListOf<String>()

                chunk.forEach { shift ->
                    val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)

                    val docRef = firestore.collection("shifts").document(generateFirestoreId())
                    val newShiftId = docRef.id

                    val shiftWithId = shift.copy(id = newShiftId, dailyRecordId = dailyRecordId)
                    val dto = shiftMapper.toDto(shiftWithId)

                    batch.set(docRef, dto)
                    chunkIds += newShiftId
                }

                batch.commit()
                ids += chunkIds
                written += chunkIds
            }

            Result.success(ids)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            if (written.isNotEmpty()) runCatching { deleteShifts(written).getOrThrow() }
            Result.failure(e)
        }
    }

    /**
     * SH-199: a copy that replaces days. The removals and the first copies go in one batch (so a
     * refusal changes nothing); further copies follow in batches the rules can check
     * ([ShiftBatches]). When a later batch fails, its predecessors are undone: the copies written
     * are deleted and the removed shifts written back as they were.
     */
    override suspend fun replaceShifts(replaced: List<Shift>, shifts: List<Shift>): Result<List<String>> {
        val deletes = replaced.map { it.id }.distinct()
        // SH-263: the replaced shifts the team had leave removal records, in the same batch.
        val removals = removalsOf(replaced.distinctBy { it.id })
        if (deletes.size + removals.size > ShiftBatches.MAX_WRITES / 2) return super.replaceShifts(replaced, shifts)
        val ids = mutableListOf<String>()
        var removed = false
        return try {
            val chunks = ShiftBatches.split(shifts, maxWrites = ShiftBatches.MAX_WRITES - deletes.size - removals.size).ifEmpty { listOf(emptyList()) }
            chunks.forEachIndexed { index, chunk ->
                val batch = firestore.batch()
                if (index == 0) {
                    deletes.forEach { batch.delete(firestore.collection("shifts").document(it)) }
                    removals.forEach { batch.set(removedRef(it.rosterId, it.shiftId), it) }
                }
                val chunkIds = chunk.map { shift ->
                    val docRef = firestore.collection("shifts").document(generateFirestoreId())
                    val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)
                    batch.set(docRef, shiftMapper.toDto(shift.copy(id = docRef.id, dailyRecordId = dailyRecordId)))
                    docRef.id
                }
                batch.commit()
                removed = true
                ids += chunkIds
            }
            Result.success(ids)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            if (removed) {
                runCatching { if (ids.isNotEmpty()) deleteShifts(ids).getOrThrow() }
                runCatching { restoreShifts(replaced).getOrThrow() }
            }
            Result.failure(e)
        }
    }

    override suspend fun deleteShifts(shiftIds: List<String>): Result<Unit> = runCatching {
        // SH-263: what they were, so the ones the team had leave removal records in the same batch.
        val before = shiftsBeforeDelete(shiftIds)
        shiftIds.distinct().chunked(ShiftBatches.MAX_WRITES / 2).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { id -> batch.delete(firestore.collection("shifts").document(id)) }
            removalsOf(chunk.mapNotNull { before[it] }).forEach { batch.set(removedRef(it.rosterId, it.shiftId), it) }
            batch.commit()
        }
    }

    // --- SH-263: published shifts removed since the last publish ---------------------------------

    private fun removedShifts(rosterId: String) =
        firestore.collection("rosters").document(rosterId).collection("removed_shifts")

    private fun removedRef(rosterId: String, shiftId: String) = removedShifts(rosterId).document(shiftId)

    /** The shifts [ids] as they are before a delete (the cache answers offline); unknown ones are left out. */
    private suspend fun shiftsBeforeDelete(ids: List<String>): Map<String, Shift> = kotlinx.coroutines.coroutineScope {
        ids.distinct().map { id -> async { getShift(id).getOrNull() } }.awaitAll().filterNotNull().associateBy { it.id }
    }

    /** The removal records [deleted] leave: those the team had (RosterPublishing.leavesRemoval). */
    private suspend fun removalsOf(deleted: Collection<Shift>): List<RemovedShift> {
        val leaving = deleted.filter(RosterPublishing::leavesRemoval)
        if (leaving.isEmpty()) return emptyList()
        val at = AppClock.now().toString()
        val by = runCatching { currentUserId() }.getOrNull()
        return leaving.map { RemovedShift.of(it, at, by) }
    }

    override fun observeRemovedShifts(rosterId: String): Flow<List<RemovedShift>> =
        removedShifts(rosterId).safeSnapshots
            .map { snapshot -> snapshot.documents.map { it.data<RemovedShift>() }.sortedBy { it.start } }
            // Owners and managers only (firestore.rules): anyone else has none to see.
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }
            .distinctUntilChanged()

    override suspend fun clearRemovedShifts(rosterId: String): Result<Unit> = runCatching {
        val ids = removedShifts(rosterId).get().documents.map { it.id }
        ids.chunked(ShiftBatches.MAX_WRITES).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { batch.delete(removedRef(rosterId, it)) }
            batch.commit()
        }
    }

    override suspend fun markReassigned(shiftId: String, publishedUserId: String?): Result<Unit> = runCatching {
        firestore.collection("shifts").document(shiftId).updateFields {
            "hasUnpublishedChanges" to true
            "publishedUserId" to publishedUserId
        }
    }

    /** Each shift written back under its id with its removal record dropped, in the same batch. */
    override suspend fun restoreShifts(shifts: List<Shift>): Result<Unit> = runCatching {
        ShiftBatches.split(shifts, maxWrites = ShiftBatches.MAX_WRITES / 2).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { shift ->
                val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)
                batch.set(firestore.collection("shifts").document(shift.id), shiftMapper.toDto(shift.copy(dailyRecordId = dailyRecordId)))
                batch.delete(removedRef(shift.rosterId, shift.id))
            }
            batch.commit()
        }
        // SH-259: a deleted shift took its notes along (onShiftDeleted).
        keepNotesOf(shifts)
    }

    private fun generateFirestoreId(): String {
        val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
        return (1..20)
            .map { chars.random() }
            .joinToString("")
    }

    override suspend fun updateShift(shift: Shift): Result<Unit> {
        return try {
            val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)

            val dto = shiftMapper.toDto(shift.copy(dailyRecordId = dailyRecordId))

            firestore.collection("shifts").document(shift.id).set(dto)
            keepNotesOf(listOf(shift))
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** SH-197: in batches the rules can check ([ShiftBatches]: at most 10 different people each). */
    override suspend fun updateShifts(shifts: List<Shift>): Result<Unit> {
        return try {
            ShiftBatches.split(shifts).forEach { chunk ->
                val batch = firestore.batch()
                chunk.forEach { shift ->
                    val dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), shift.date)
                    batch.set(firestore.collection("shifts").document(shift.id), shiftMapper.toDto(shift.copy(dailyRecordId = dailyRecordId)))
                }
                batch.commit()
            }
            keepNotesOf(shifts)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteShift(shiftId: String): Result<Unit> = deleteShifts(listOf(shiftId))


    override suspend fun getUserShiftsInWorkplace(
        workplaceId: String,
        userId: String,
        startDate: String,
        endDate: String
    ): Result<List<Shift>> = runCatching {
        workplaceShifts(workplaceId, startDate, endDate).filter { it.userId == userId }
    }

    /**
     * SH-237: the same per-month roster queries, kept by who worked each shift: a covered shift
     * (`actualUserId`) is in the month's documents already, so no other query (or rule) is needed.
     */
    override suspend fun getShiftsWorkedInWorkplace(
        workplaceId: String,
        userId: String,
        startDate: String,
        endDate: String
    ): Result<List<Shift>> = runCatching {
        workplaceShifts(workplaceId, startDate, endDate).filter { it.workerId == userId }
    }

    /** The workplace's shifts between two ISO dates (inclusive), one query per roster month, by planned start. */
    private suspend fun workplaceShifts(workplaceId: String, startDate: String, endDate: String): List<Shift> =
        RosterIds.monthsBetween(startDate, endDate).flatMap { (year, month) ->
            firestore.collection("shifts")
                .where { "rosterId" equalTo RosterIds.of(workplaceId, year, month) }
                .get().documents
                .map { shiftMapper.toDomain(it.data<ShiftDto>()) }
        }.filter { it.date in startDate..endDate }.sortedBy { it.plannedTime.start }

    override suspend fun findOverlappingShifts(userId: String, shift: Shift): Result<List<ShiftOverlap>> = runCatching {
        val result = functions.call(
            "findShiftConflicts",
            buildJsonObject {
                put("userId", userId)
                put("shiftId", shift.id)
                put("start", shift.plannedTime.start)
                put("end", shift.plannedTime.end)
            }
        )
        overlapsJson.decodeFromJsonElement(FindShiftConflictsResult.serializer(), result).conflicts.map {
            ShiftOverlap(it.shiftId, TimeRange(it.start, it.end), it.sameOwner, it.workplaceName)
        }
    }

    override suspend fun recordWorker(shiftId: String, workerId: String?, editorUserId: String): Result<Unit> = runCatching {
        firestore.collection("shifts").document(shiftId).updateFields {
            "actualUserId" to workerId
            "notWorked" to (workerId == null)
            "metadata.updatedAt" to AppClock.now().toString()
            "metadata.updatedBy" to editorUserId
        }
    }
    override suspend fun recordActualWork(
        shiftId: String,
        actualUserId: String?,
        notWorked: Boolean,
        noShowReason: String?,
        editorUserId: String
    ): Result<Unit> = runCatching {
        // SH-259: the reason goes to the notes (managers only), the shift keeps none; the split's
        // note there stays as it is.
        val shift = getShift(shiftId).getOrThrow()
        val kept = getManagerNote(shiftId).getOrNull()
        val notes = ShiftManagerNotes(
            shiftId, shift.rosterId,
            noShowReason = noShowReason?.takeIf { notWorked },
            splitReason = kept?.splitReason ?: shift.split?.reason
        )
        val batch = firestore.batch()
        batch.updateFields(firestore.collection("shifts").document(shiftId)) {
            "actualUserId" to actualUserId
            "notWorked" to notWorked
            "noShowReason" to null
            "metadata.updatedAt" to AppClock.now().toString()
            "metadata.updatedBy" to editorUserId
        }
        if (!notes.isEmpty) batch.set(notesRef(shiftId), notesDto(notes))
        else if (kept != null) batch.delete(notesRef(shiftId))
        batch.commit()
    }

    /** SH-236: the shortened first part and the new second part in one batch (all or nothing). */
    override suspend fun splitShift(original: Shift, first: Shift, second: Shift): Result<String> = runCatching {
        val batch = firestore.batch()
        val firstRecord = DailyRecordIds.of(RosterIds.workplaceIdOf(first.rosterId), first.date)
        batch.set(firestore.collection("shifts").document(first.id), shiftMapper.toDto(first.copy(dailyRecordId = firstRecord)))
        val ref = firestore.collection("shifts").document(generateFirestoreId())
        val secondRecord = DailyRecordIds.of(RosterIds.workplaceIdOf(second.rosterId), second.date)
        batch.set(ref, shiftMapper.toDto(second.copy(id = ref.id, dailyRecordId = secondRecord)))
        // SH-259: the split's note on both parts, in the managers' notes.
        listOfNotNull(ShiftManagerNotes.of(first), ShiftManagerNotes.of(second.copy(id = ref.id)))
            .forEach { batch.set(notesRef(it.shiftId), notesDto(it)) }
        batch.commit()
        ref.id
    }

    /** SH-236: the original shift written back and the second part removed in one batch. */
    override suspend fun undoSplit(restored: Shift, secondId: String): Result<Unit> = runCatching {
        val batch = firestore.batch()
        val record = DailyRecordIds.of(RosterIds.workplaceIdOf(restored.rosterId), restored.date)
        batch.set(firestore.collection("shifts").document(restored.id), shiftMapper.toDto(restored.copy(dailyRecordId = record)))
        batch.delete(firestore.collection("shifts").document(secondId))
        // SH-263: a second part the team already had leaves a removal record.
        removalsOf(listOfNotNull(getShift(secondId).getOrNull())).forEach { batch.set(removedRef(it.rosterId, it.shiftId), it) }
        // SH-259: no split, no split note; the second part's notes go with it.
        val notes = ShiftManagerNotes.of(restored)
        if (notes != null) batch.set(notesRef(restored.id), notesDto(notes)) else batch.delete(notesRef(restored.id))
        batch.delete(notesRef(secondId))
        batch.commit()
    }

    override suspend fun findBusyElsewhere(
        workplaceId: String,
        userIds: List<String>,
        startDate: String,
        endDate: String
    ): Result<List<BusyElsewhere>> = runCatching {
        if (userIds.isEmpty()) return@runCatching emptyList()
        // The function takes up to BUSY_BATCH people at a time (a large team: several calls).
        userIds.distinct().chunked(BUSY_BATCH).flatMap { batch ->
            val result = functions.call(
                "findBusyElsewhere",
                buildJsonObject {
                    put("workplaceId", workplaceId)
                    put("userIds", kotlinx.serialization.json.JsonArray(batch.map { JsonPrimitive(it) }))
                    put("from", startDate)
                    put("to", endDate)
                }
            )
            overlapsJson.decodeFromJsonElement(FindBusyElsewhereResult.serializer(), result).busy.map {
                BusyElsewhere(it.userId, it.date, TimeRange(it.start, it.end), it.sameOwner, it.workplaceName)
            }
        }.sortedBy { it.plannedTime.start }
    }

    /** SH-165: the swapAvailability function (employees never read colleagues' availability). */
    override suspend fun swapAvailability(shiftId: String): Result<Map<String, com.evoapps.evoshift.domain.model.AvailabilityStatus>> = runCatching {
        val result = functions.call("swapAvailability", buildJsonObject { put("shiftId", shiftId) })
        overlapsJson.decodeFromJsonElement(SwapAvailabilityResult.serializer(), result).availability
            .mapNotNull { (uid, status) ->
                com.evoapps.evoshift.domain.model.AvailabilityStatus.entries.find { it.name == status }?.let { uid to it }
            }.toMap()
    }

    override suspend fun updateSwapRequest(shiftId: String, request: SwapRequest?): Result<Unit> {
        return try {
            val requestDto = request?.let { swapRequestMapper.toDto(it) }
            // Using set with merge or update to handle null correctly in Firestore KMP
            if (requestDto == null) {
                firestore.collection("shifts").document(shiftId).updateFields { "swapRequest" to null }
            } else {
                firestore.collection("shifts").document(shiftId).updateFields { "swapRequest" to requestDto }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** SH-164: the swap approval's Undo, in one write. */
    override suspend fun restoreSwap(
        shiftId: String,
        userId: String?,
        isOpen: Boolean,
        conflictMeta: ConflictMetadata?,
        request: SwapRequest?
    ): Result<Unit> = runCatching {
        firestore.collection("shifts").document(shiftId).updateFields {
            "userId" to userId
            "isOpen" to isOpen
            "conflictMetadata" to conflictMeta?.let { conflictMetadataMapper.toDto(it) }
            "swapRequest" to request?.let { swapRequestMapper.toDto(it) }
        }
    }

    override suspend fun updateActualTime(shiftId: String, actual: TimeRange?, editorUserId: String): Result<Unit> = runCatching {
        val ref = firestore.collection("shifts").document(shiftId)
        val current = ref.get().data<ShiftDto>()
        val dto = ShiftMapper.withClockAudit(
            actual?.let { ShiftTimeDto(it.start, it.end) } ?: ShiftTimeDto("", ""),
            ActualTimeStamps.after(current.userId, current.actual, actual, editorUserId)
        )
        // Field by field, never `actual.breaks` (SH-116): the clock functions and managers change
        // breaks, the rules refuse the person's own change of them, and rewriting the list would
        // not give back exactly the stored maps.
        ref.updateFields {
            "actual.start" to dto.start
            "actual.end" to dto.end
            "actual.startSource" to dto.startSource
            "actual.startKioskId" to dto.startKioskId
            "actual.startSyncedLate" to dto.startSyncedLate
            "actual.startDeviceTime" to dto.startDeviceTime
            "actual.endSource" to dto.endSource
            "actual.endKioskId" to dto.endKioskId
            "actual.endSyncedLate" to dto.endSyncedLate
            "actual.endDeviceTime" to dto.endDeviceTime
            "actual.startLocationCheck" to dto.startLocationCheck
            "actual.startDistanceM" to dto.startDistanceM
            "actual.endLocationCheck" to dto.endLocationCheck
            "actual.endDistanceM" to dto.endDistanceM
            "metadata.updatedAt" to AppClock.now().toString()
            "metadata.updatedBy" to editorUserId
        }
    }

    /**
     * SH-106: the clockShift function records the time with the geofence check. The position goes
     * to the function only (it keeps the result and the distance, never the coordinates); its
     * refusals come back as [ClockException].
     */
    override suspend fun clockWithLocation(shiftId: String, action: ClockAction, reading: LocationReading): Result<ClockOutcome> =
        callClockShift(reading) {
            put("shiftId", shiftId)
            put("action", action.name)
        }

    /** SH-165: the clockShift function clears its own clock-out of the last few minutes (UNDO_OUT). */
    override suspend fun undoClockOut(shiftId: String): Result<Unit> =
        callClockShift(LocationReading.Unavailable(com.evoapps.evoshift.domain.model.LocationUnavailable.UNSUPPORTED)) {
            put("shiftId", shiftId)
            put("action", UNDO_OUT)
        }.map { }

    override suspend fun clockInUnplanned(workplaceId: String, reading: LocationReading): Result<ClockOutcome> =
        callClockShift(reading) {
            put("workplaceId", workplaceId)
            put("action", ClockAction.IN.name)
        }

    private suspend fun callClockShift(
        reading: LocationReading,
        fields: kotlinx.serialization.json.JsonObjectBuilder.() -> Unit
    ): Result<ClockOutcome> = try {
        val result = functions.call(
            "clockShift",
            buildJsonObject {
                fields()
                when (reading) {
                    is LocationReading.Fix -> put("location", buildJsonObject {
                        put("latitude", reading.location.latitude)
                        put("longitude", reading.location.longitude)
                        reading.accuracyMeters?.let { put("accuracyM", it) }
                    })
                    is LocationReading.Unavailable -> put("unavailable", reading.reason.name)
                }
            }
        )
        val clocked = overlapsJson.decodeFromJsonElement(ClockShiftResult.serializer(), result)
        Result.success(
            ClockOutcome(
                LocationCheck.fromName(clocked.locationCheck), clocked.distanceM,
                unplanned = clocked.unplanned?.toDomain(), shiftId = clocked.shiftId
            )
        )
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(clockRefusalOf(e))
    } catch (e: Exception) {
        Result.failure(e)
    }

    override fun observeCoveredShifts(userId: String): Flow<List<Shift>> =
        firestore.collection("shifts")
            .where { "unplanned.userId" equalTo userId }
            .snapshots
            .map { snapshot -> snapshot.documents.map { shiftMapper.toDomain(it.data<ShiftDto>()) }.sortedBy { it.date } }
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }

    override suspend fun reviewClock(shiftId: String, action: ClockReviewAction, start: String?, end: String?): Result<Boolean> = try {
        val result = functions.call("reviewClock", buildJsonObject {
            put("shiftId", shiftId)
            put("action", action.name)
            start?.let { put("start", it) }
            end?.let { put("end", it) }
        })
        Result.success(overlapsJson.decodeFromJsonElement(ReviewClockResult.serializer(), result).deleted)
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(reviewRefusalOf(e))
    } catch (e: Exception) {
        Result.failure(e)
    }

    override suspend fun reportWorkHours(shiftId: String, start: String, end: String): Result<Unit> = try {
        functions.call("reportWorkHours", buildJsonObject {
            put("shiftId", shiftId)
            put("start", start)
            put("end", end)
        })
        Result.success(Unit)
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(workHoursRefusalOf(e))
    } catch (e: Exception) {
        Result.failure(e)
    }

    override suspend fun assignUser(
        shiftId: String,
        userId: String?,
        conflictMeta: ConflictMetadata?,
        borrowedFrom: String?
    ): Result<Unit> {
        return try {
            firestore.collection("shifts").document(shiftId).updateFields {
                "userId" to userId
                // SH-267: borrowed from another workplace of the group, or no longer.
                "borrowedFromWorkplaceId" to borrowedFrom?.takeIf { userId != null }
                // Someone is on the shift now: it is not offered to the team any more.
                if (userId != null) "isOpen" to false
                "conflictMetadata" to conflictMeta?.let { conflictMetadataMapper.toDto(it) }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun claimShift(shiftId: String, claimantId: String): Result<ClaimOutcome> = runCatching {
        val result = functions.call(
            "claimShift",
            buildJsonObject {
                put("shiftId", shiftId)
                put("claimantId", claimantId)
            }
        )
        val claim = overlapsJson.decodeFromJsonElement(ClaimShiftResult.serializer(), result)
        when (claim.outcome) {
            "ASSIGNED" -> ClaimOutcome.ASSIGNED
            "PENDING_APPROVAL" -> ClaimOutcome.PENDING_APPROVAL
            else -> throw ClaimRefusedException(
                ClaimRefusal.entries.find { it.name == claim.reason } ?: ClaimRefusal.NOT_AVAILABLE
            )
        }
    }

    override fun observePendingSwaps(rosterId: String): Flow<List<Shift>> {
        return firestore.collection("shifts")
            .where { "rosterId" equalTo rosterId }
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents
                    .map { it.data<ShiftDto>() }
                    .filter { it.swapRequest != null }
                    .map { shiftMapper.toDomain(it) }
                    .sortedBy { it.date }
            }
            .catch { e ->
                if (e.isPermissionDenied()) {
                    emit(emptyList())
                } else {
                    throw e
                }
            }
            // Snapshots fire from the cache and again from the server with the same data.
            .distinctUntilChanged()
    }
}

private val overlapsJson = Json { ignoreUnknownKeys = true }

/** findBusyElsewhere's limit of people per call (functions/busyElsewhere.js). */
private const val BUSY_BATCH = 100

/** SH-255: the teamRoster function's answer. */
@Serializable
private data class TeamRosterResult(val shifts: List<ShiftDto> = emptyList())

@Serializable
private data class FindBusyElsewhereResult(val busy: List<BusyElsewhereDto> = emptyList())

@Serializable
private data class BusyElsewhereDto(
    val userId: String,
    val date: String,
    val start: String,
    val end: String,
    val sameOwner: Boolean,
    val workplaceName: String? = null
)

/** SH-165: the swapAvailability function's answer: colleague uid -> AVAILABLE / UNAVAILABLE. */
@Serializable
private data class SwapAvailabilityResult(val availability: Map<String, String> = emptyMap())

/** SH-165: the clockShift action that takes the caller's own recent clock-out back. */
private const val UNDO_OUT = "UNDO_OUT"

@Serializable
private data class ClockShiftResult(
    val action: String? = null,
    val locationCheck: String? = null,
    val distanceM: Int? = null,
    val shiftId: String? = null,
    val unplanned: UnplannedResultDto? = null
)

/** SH-134: an unplanned clock-in in a clock function's answer. */
@Serializable
internal data class UnplannedResultDto(val kind: String, val status: String? = null, val coveredName: String? = null) {
    fun toDomain(): UnplannedClockIn? = UnplannedKind.fromName(kind)?.let { UnplannedClockIn(it, coveredName) }
}

@Serializable
private data class ReviewClockResult(val shiftId: String = "", val action: String = "", val deleted: Boolean = false)

/** reviewClock's refusals as [ClockReviewException]. */
/** SH-259: the managers' notes on shifts (firestore.rules, functions/shiftManagerNotes.js). */
internal const val MANAGER_NOTES = "shift_manager_notes"

internal fun reviewRefusalOf(e: CloudFunctionsException): Exception {
    val details = e.details as? JsonObject ?: return e
    val reason = ClockReviewException.Reason.entries.find { it.name == (details["reason"] as? JsonPrimitive)?.contentOrNull } ?: return e
    return ClockReviewException(reason)
}

/** SH-257: reportWorkHours' refusals as [com.evoapps.evoshift.domain.model.WorkHoursReportException]. */
internal fun workHoursRefusalOf(e: CloudFunctionsException): Exception {
    val details = e.details as? JsonObject ?: return e
    val name = (details["reason"] as? JsonPrimitive)?.contentOrNull
    val reason = com.evoapps.evoshift.domain.model.WorkHoursReportException.Reason.entries.find { it.name == name } ?: return e
    return com.evoapps.evoshift.domain.model.WorkHoursReportException(reason)
}

/** clockShift's refusals ({reason, distanceM, radiusM, unavailable} in the details) as [ClockException]. */
internal fun clockRefusalOf(e: CloudFunctionsException): Exception {
    val details = e.details as? JsonObject ?: return e
    fun str(key: String) = (details[key] as? JsonPrimitive)?.contentOrNull
    fun int(key: String) = (details[key] as? JsonPrimitive)?.intOrNull
    val reason = ClockException.Reason.entries.find { it.name == str("reason") } ?: return e
    return ClockException(
        reason,
        distanceMeters = int("distanceM"),
        radiusMeters = int("radiusM"),
        locationUnavailable = LocationUnavailable.fromName(str("unavailable"))
    )
}

@Serializable
private data class ClaimShiftResult(val outcome: String, val reason: String? = null)

@Serializable
private data class FindShiftConflictsResult(val conflicts: List<ShiftConflictDto> = emptyList())

@Serializable
private data class ShiftConflictDto(
    val shiftId: String,
    val start: String,
    val end: String,
    val sameOwner: Boolean,
    val workplaceName: String? = null
)
